<template>
  <div class="checkout-container">
    <!-- Back to all gift cards or deposit detail  start-->
    <router-link :to="{ name: feature, params: storeId }">
      <div class="back-to-all-gift-cards">
        <v-icon>chevron_left</v-icon>
        <span v-if="feature === 'GiftCard'">
          {{ $vuetify.lang.t("$vuetify.checkout.backToAllGiftDetails") }}
        </span>
        <span v-else-if="feature === 'Deposit'">
          {{ $vuetify.lang.t("$vuetify.deposit.backToDepositDetail") }}
        </span>
      </div>
    </router-link>
    <!-- Back to all gift cards or deposit detail  end-->

    <spacer size="xl"></spacer>
    <spacer size="m"></spacer>
    <section class="fw-bold fs-l">
      {{ $vuetify.lang.t("$vuetify.checkout.reviewYourCart") }}
    </section>
    <spacer size="s" border-bottom></spacer>
    <spacer size="m"></spacer>

    <!-- Gift Card Checkout Detail start -->
    <template v-if="feature === 'GiftCard'">
      <gift-card-review
        :card-details="cardDetails"
        :get-amount="getAmount"
        :get-bonus-by-amount="getBonusByAmount"
      />
    </template>
    <!-- Gift Card Checkout Detail end -->

    <!-- Deposit Checkout Detail start -->
    <template v-else-if="feature === 'Deposit'">
      <deposit-review
        :get-amount="getAmount"
        :get-deposit-bonus-by-amount="getDepositBonusByAmount"
        :get-deposit-points-by-amount="getDepositPointsByAmount"
        :deposit-details="depositDetails"
      />
    </template>

    <template v-if="paymentOptions.length > 0">
      <spacer size="m"></spacer>
      <spacer size="xl"></spacer>
      <section class="fw-bold fs-l">
        {{ $vuetify.lang.t("$vuetify.checkout.choosePaymentOption") }}
      </section>
      <spacer size="m"></spacer>
      <div id="payment-option-container" style="position: relative;">
        <v-select
          ref="paymentSelect"
          attach="#payment-option-container"
          v-model="paymentOption"
          :items="paymentOptions"
          item-text="text"
          item-value="value"
          :label="$vuetify.lang.t('$vuetify.checkout.selectPaymentMethod')"
          rounded
          filled
          @change="onPaymentOptionChange($event)"
          :menu-props="{ closeOnContentClick: false }"
        >
          <template v-slot:item="{ item, on }">
            <v-list-item :key="item.value" v-on="on">
              <v-list-item-icon v-if="item.icon">
                <v-icon>{{ item.icon }}</v-icon>
              </v-list-item-icon>
              <template v-if="item.imgSrc">
                <img :height="14" :width="20" :src="item.imgSrc" />
              </template>
              <v-list-item-content style="text-align: left; margin-left: 10px;">
                <v-list-item-title>{{ item.text }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-select>
      </div>
    </template>

    <form style="z-index: 1">
      <div v-if="showStripePaymentForm">
        <spacer size="m"></spacer>
        <div class="fw-bold fs-l">
          {{ $vuetify.lang.t("$vuetify.checkout.paymentInformation") }}
        </div>
        <spacer size="m"></spacer>

        <payment-details
          :using-we-chat.sync="usingWeChat"
          :card-ready.sync="cardReady"
          ref="payment"
        />
        <spacer size="m"></spacer>
        <masked-input
          ref="Name"
          :label="$vuetify.lang.t('$vuetify.forms.billing.nameOnCard')"
          :hint="$vuetify.lang.t('$vuetify.forms.billing.nameHint')"
          icon="person_outline"
          validation="nonempty"
          required
          :errorMessages="errors['nameError']"
          :raw.sync="billingInfo.billingName"
          key="billingName"
        />
        <spacer size="m"></spacer>
        <div style="display: flex; gap: 10px;">
          <div id="bc-select" style="position: relative;flex: 1;">
            <v-autocomplete
              :height="52"
              id="bc"
              :items="countryImport"
              item-text="country"
              :label="$vuetify.lang.t('$vuetify.forms.country')"
              @change="handleCountryChange"
              v-model="selectedCountry"
              :error-messages="errors.country"
              required
              return-object
              filled
              rounded
              attach="#bc-select"
              :input-props="{
                autocomplete: 'off',
                type: 'text',
              }"
            ></v-autocomplete>
          </div>

          <div style="flex: 1;">
            <!-- todo error need to be fixed -->
            <masked-input
              ref="PostalCode"
              :label="
                $vuetify.lang.current === 'zhHans'
                  ? $vuetify.lang.t('$vuetify.forms.otherPostalCode')
                  : selectedCountry.name ||
                    $vuetify.lang.t('$vuetify.forms.otherPostalCode')
              "
              :hint="$vuetify.lang.t('$vuetify.forms.billing.postalCodeHint')"
              validation="nonempty"
              required
              :errorMessages="errors['nameError']"
              :raw.sync="billingInfo.postalCode"
            />
          </div>
        </div>
      </div>
      <template v-if="showCustomPaymentForm">
        <spacer size="l"></spacer>
        <div class="fw-bold fs-l">
          {{ $vuetify.lang.t("$vuetify.checkout.paymentInformation") }}
        </div>
        <AddPaymentMethod ref="AddPaymentMethod" />
      </template>
      <spacer size="m"></spacer>

      <masked-input
        ref="Phone"
        :label="$vuetify.lang.t('$vuetify.forms.billing.phone')"
        :hint="$vuetify.lang.t('$vuetify.forms.billing.phoneHint')"
        icon="smartphone"
        mask="phoneNumber"
        validation="validatePhone"
        :error-messages="errors['phoneError']"
        :raw.sync="billingInfo.billingTelephone"
        limitRawLength="10"
        :required="true"
      />
      <spacer size="xl"></spacer>
      <div class="fw-bold fs-m">
        {{ $vuetify.lang.t("$vuetify.checkout.receipt") }}
      </div>

      <spacer size="m"></spacer>
      <masked-input
        ref="Email"
        :label="$vuetify.lang.t('$vuetify.forms.billing.email')"
        :hint="$vuetify.lang.t('$vuetify.forms.billing.emailHint')"
        icon="mail_outline"
        :raw.sync="billingInfo.billingEmail"
      />

      <v-checkbox
        v-if="cardDetails.receiverEmail"
        :label="
          $vuetify.lang.t('$vuetify.checkout.receiptEmailSameAsRecipientEmail')
        "
        v-model="receiptEmailSameAsRecipientEmail"
        @change="onReceiptEmailSameAsRecipientEmailChange"
        class="fs-xs fc-black"
      />
    </form>

    <spacer size="m"></spacer>
    <spacer size="xl"></spacer>

    <div class="row center-xs">
      <v-btn
        min-width="250"
        style="padding:0 40px "
        rounded
        color="primary"
        @click="handlePayButtonClick"
        :disabled="!readyToProceed"
      >
        <v-icon style="margin-right: 10px;">lock</v-icon>
        {{ $vuetify.lang.t("$vuetify.checkout.pay") }}
        {{ getAmount | currency(true) }}
        {{
          feature == "Coupon"
            ? $vuetify.lang.t("$vuetify.common.includingTax")
            : null
        }}
      </v-btn>
    </div>
    <spacer size="xl"></spacer>
    <spacer size="xl"></spacer>
    <spacer size="s"></spacer>
    <spacer size="s"></spacer>

    <iFrame :onConfirm="purchaseCard" ref="iFrame" />
  </div>
</template>

<script>
import PaymentDetails from "../common/PaymentDetails";
import maskedInput from "../common/maskedInput";
import iFrame from "../common/iFrame";
import { get } from "lodash";
import { loadStripe } from "@stripe/stripe-js";
import { mapMutations, mapGetters } from "vuex";
import Spacer from "../common/Spacer.vue";
import { countries } from "../../countries.js";
import AddPaymentMethod from "../common/AddPaymentMethod.vue";
import GiftCardReview from "./checkout/gift-card-review.vue";
import DepositReview from "./checkout/deposit-review.vue";

export default {
  components: {
    PaymentDetails,
    maskedInput,
    iFrame,
    AddPaymentMethod,
    Spacer,
    GiftCardReview,
    DepositReview,
  },

  data: () => ({
    cardReady: false,
    usingWeChat: false,
    paymentCards: [],

    errors: {},
    inprogress: false,
    showPaymentForm: false,
    selectedCountry: {},
    countryImport: countries,

    paymentOption: null,

    paymentOptions: [],

    receiptEmailSameAsRecipientEmail: false,

    stripeInstance: null,
    applePayRequest: null,
  }),

  computed: {
    showStripePaymentForm() {
      if (
        !this.authorizationId &&
        this.paymentOptions.length > 0 &&
        this.paymentOption === "add_payment"
      ) {
        return true;
      }

      if (!this.authorizationId && this.paymentOptions.length === 0) {
        return true;
      }
      return false;
    },

    showCustomPaymentForm() {
      if (
        this.authorizationId &&
        this.paymentOptions.length >= 0 &&
        this.paymentOption === "add_payment"
      ) {
        return true;
      }
      if (this.authorizationId && this.paymentOptions.length === 0) {
        return true;
      }
      return false;
    },

    infoEntered() {
      if (this.paymentOption === "apple_pay") {
        return true;
      }

      const {
        billingName,
        billingTelephone,
        country,
        postalCode,
      } = this.billingInfo;

      if (this.showStripePaymentForm) {
        if (
          !this.cardReady ||
          !billingName ||
          !billingTelephone ||
          !country ||
          !postalCode
        ) {
          return false;
        }
      }

      if (this.showCustomPaymentForm) {
        return true;
      }

      if (this.paymentOption !== "add_payment") {
        if (this.billingInfo.paymentMethodId) {
          return true;
        }
      }

      return true;
    },
    readyToProceed() {
      return (
        this.infoEntered &&
        !Object.keys(this.errors || {}).length &&
        !this.inprogress
      );
    },

    isStoreSupportsStripe() {
      return this?.build?.settings?.paymentProvider === "STRIPE";
    },

    isBrowserSupportsApplePay() {
      return window.ApplePaySession && window.ApplePaySession.canMakePayments();
    },

    isApplePayAvailable() {
      return this.isStoreSupportsStripe && this.isBrowserSupportsApplePay;
    },

    activeCoupons() {
      return get(this.couponDetails, "coupons", []).filter(
        (coupon) => !!coupon.using
      );
    },

    stripeKey() {
      return this.isTest
        ? "pk_test_yTuFHGcSGCBm65aYYT5Vt0Ee"
        : "pk_live_ppArwjpEtPJBVq0xPuwlC3ro";
    },

    ...mapGetters({
      storeId: "getStoreID",
      build: "getBuild",
      origin: "origin",
      feature: "getFeature",
      getAmount: "getAmount",
      cardAmont: "getCardAmont",
      couponDetails: "getCouponDetails",
      depositAmount: "getDepositAmount",
      depositDetails: "getDepositInfo",
      cardDetails: "getCardDetails",
      billingInfo: "getBillingInfo",
      purchaseInfo: "getPurchaseInfo",
      authorizationId: "getAuthId",
      isTest: "isTest",
      getStore: "getStore",
      getBonusByAmount: "getBonusByAmount",
      getDepositBonusByAmount: "getDepositBonusByAmount",
      getDepositPointsByAmount: "getDepositPointsByAmount",
    }),
  },

  watch: {
    readyToProceed() {
      if (this.readyToProceed)
        this.$bus.$emit(
          "showAlert",
          this.$vuetify.lang.t("$vuetify.paymentDetails.ready"),
          "primary"
        );
    },
    getAmount() {
      if (this.isApplePayAvailable && this.applePayRequest) {
        this.initializeApplePay();
      }
    },
  },

  methods: {
    async payWithApplePay() {
      let _this = this;

      try {
        if (!this.applePayRequest) {
          _this.$bus.$emit(
            "showAlert",
            "Apple Pay is initializing. Please try again in a moment.",
            "error"
          );
          return;
        }

        this.applePayRequest.show();
      } catch (error) {
        _this.inprogress = false;
        console.error("Apple Pay error:", error);
        _this.$bus.$emit(
          "showAlert",
          error.message || "Apple Pay failed. Please try again.",
          "error"
        );
      }
    },

    async initializeApplePay() {
      try {
        // Load Stripe.js v3
        const stripe = await loadStripe(this.stripeKey);
        this.stripeInstance = stripe;

        // Create payment request
        const paymentRequestConfig = {
          country: this.getStore.country || "CA",
          currency: ((this.getStore.country || "CA") + "D").toLowerCase(),
          total: {
            label: this.getStore.name,
            amount: Math.round((this.getAmount || 0) * 100), // Convert to cents
          },
          requestPayerName: false,
          requestPayerEmail: false,
        };

        const paymentRequest = stripe.paymentRequest(paymentRequestConfig);

        // Check if Apple Pay is available
        const canMakePaymentResult = await paymentRequest.canMakePayment();

        if (!canMakePaymentResult || !canMakePaymentResult.applePay) {
          console.error("Apple Pay not available:", canMakePaymentResult);
          return;
        }
        this.applePayRequest = paymentRequest;

        // Handle payment method received
        const _this = this;
        paymentRequest.on("paymentmethod", async (ev) => {
          // Set the payment method ID
          _this.setBillingInfo({
            ..._this.billingInfo,
            paymentMethodId: ev.paymentMethod.id,
          });

          // Complete the payment request
          ev.complete("success");

          // Proceed with purchase
          _this.purchaseCard();
        });

        // Handle cancel
        paymentRequest.on("cancel", () => {
          _this.inprogress = false;
          _this.$bus.$emit(
            "showAlert",
            "Apple Pay was canceled. If this wasn't intentional, please try again."
          );
        });
      } catch (error) {
        console.error("Failed to initialize Apple Pay:", error);
      }
    },

    async handlePayButtonClick() {
      if (this.paymentOption === "apple_pay") {
        return this.payWithApplePay();
      }

      // Execute validation first
      this.checkValidation();

      if (Object.keys(this.errors).length) {
        return;
      }

      // Save card if user is logged in and has payment cards
      if (
        this.authorizationId &&
        this.paymentCards &&
        this.paymentCards.length > 0 &&
        this.showPaymentForm &&
        this.$refs.AddPaymentMethod &&
        typeof this.$refs.AddPaymentMethod.saveCard === "function"
      ) {
        const saveSuccess = await this.$refs.AddPaymentMethod.saveCard();

        // If save fails, do not continue with the payment process
        if (!saveSuccess) {
          return;
        }

        // After successful save, refetch payment methods and select the latest one
        await this.fetchPaymentMethods(true);
      }
      // After successful save, proceed with the payment process
      this.purchaseCard();
    },

    onReceiptEmailSameAsRecipientEmailChange() {
      const newEmail = this.receiptEmailSameAsRecipientEmail
        ? this.cardDetails.receiverEmail
        : null;

      // Update via Vuex mutation to ensure store is updated and reactivity is maintained
      this.setBillingInfo({
        ...this.billingInfo,
        billingEmail: newEmail,
      });
    },
    getSrc(src) {
      return `${window.location.origin}/giftcard${src}`;
    },

    onPaymentOptionChange(selectedValue) {
      const value = selectedValue || this.paymentOption;
      if (value === "add_payment") {
        this.showPaymentForm = true;
        this.setBillingInfo({
          ...this.billingInfo,
          paymentMethodId: null,
        });
      } else if (value === "apple_pay") {
        this.showPaymentForm = false;
        this.setBillingInfo({
          ...this.billingInfo,
          paymentMethodId: null,
        });
      } else {
        this.showPaymentForm = false;
        const selectedCard = this.paymentCards?.find(
          (card) => card.paymentMethodId === value
        );
        if (selectedCard) {
          this.setBillingInfo({
            ...this.billingInfo,
            paymentMethodId: selectedCard.paymentMethodId,
          });
        }
      }
    },

    onPaymentMethod(method) {
      const val =
        this.billingInfo.paymentMethodId !== method.paymentMethodId
          ? method.paymentMethodId
          : "";

      this.$set(this.billingInfo, "paymentMethodId", val);
      this.$delete(this.errors, "paymentMethodId");
    },
    fetchPaymentMethods(useLast = false) {
      if (this.authorizationId) {
        return this.$api
          .get("/owa/customer/paymentMethod")
          .then((res) => {
            this.paymentCards = res.data || [];
            if (useLast) {
              const last = this.paymentCards[this.paymentCards.length - 1];
              this.onPaymentMethod(last);
            } else {
              this.onPaymentMethod(this.paymentCards[0]);
            }
          })
          .catch(() => {
            this.paymentCards = [];
          });
      } else {
        this.paymentCards = [];
        return Promise.resolve([]);
      }
    },

    initPaymentOptions() {
      // Only show dropdown if there are saved cards OR Apple Pay is available
      if (
        (this.authorizationId && this.paymentCards.length > 0) ||
        this.isApplePayAvailable
      ) {
        this.paymentOptions.push({
          text: this.$vuetify.lang.t("$vuetify.checkout.addPayment"),
          value: "add_payment",
          icon: "add_circle_outline",
        });
      }

      if (this.isApplePayAvailable) {
        this.paymentOptions.unshift({
          text: "Apple Pay",
          value: "apple_pay",
          imgSrc: this.getSrc("/img/Dark Color/apple-pay.png"),
        });
      }

      let options = [];
      this.paymentCards.forEach((card) => {
        const { brand = "", last4 = "", paymentMethodId = "" } = card;
        options.push({
          text: brand + " **** " + last4,
          value: paymentMethodId,
          imgSrc: this.getSrc(`/img/Dark Color/${brand}.png`),
          icon: null,
        });
      });
      this.paymentOptions.unshift(...options);
    },

    UUID() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(
        c
      ) {
        var r = (Math.random() * 16) | 0,
          v = c == "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    },
    async buildPayload() {
      let info = {
        ...this.billingInfo,
        storeId: this.storeId,
        transactionId: this.UUID(),
        postalCode: (this.billingInfo.postalCode || "")
          .replace(/-/g, "")
          .replace(/ /g, ""),
        returnUrl: `${window.location.origin}/giftcard/${this.storeId}/3ds_success_handler`,
      };

      console.log("build payloadinfo ====>", info);
      console.log("billingInfo before buildPayload ====>", this.billingInfo);
      console.log("paymentOption ====>", this.paymentOption);

      // Attach the users input data from the current feature
      if (this.feature === "GiftCard") {
        info = {
          ...info,
          ...this.cardDetails,
          // For some reason the backend wants this
          amount: this.getAmount,
          otherAmount: this.getAmount,
        };
      } else if (this.feature === "Deposit") {
        info = {
          ...info,
          ...this.depositDetails,
        };
      } else {
        info = {
          ...info,
          amount: this.getAmount,
          telephone: this.couponDetails.telephone,
          cart: this.activeCoupons.map((coupon) => {
            return {
              code: coupon.code,
              qty: coupon.using,
            };
          }),
        };
      }

      if (this.usingWeChat) {
        info.paymentType = "WECHAT";
      } else if (!this.billingInfo.paymentMethodId) {
        info.paymentType = "ONLINE";
        info.paymentToken = await this.$refs.payment.retrieveStripeToken();
      }
      // Note: If paymentMethodId exists (e.g., from Apple Pay), paymentType is not set here
      // Backend may automatically determine paymentType based on paymentMethodId format

      console.log("Final payload before sending ====>", info);
      console.log("paymentType in final payload ====>", info.paymentType);

      return info;
    },

    determinePurchaseMethod() {
      if (this.feature === "GiftCard") return this.purchaseGiftcard;

      if (this.feature === "Deposit") return this.purchaseDeposit;

      if (this.feature === "Coupon") return this.purchaseCoupon;
    },
    purchaseGiftcard(payload) {
      return this.$api.post(`/giftcard/selfcare/purchase`, payload);
    },
    purchaseCoupon(payload) {
      return this.$api.post("/rwa/selfcare/coupon/purchase", payload);
    },
    purchaseDeposit(payload) {
      return this.$api.post(`/rwa/selfcare/deposit/purchase`, payload);
    },

    async makePurchase() {
      this.inprogress = true;

      const info = await this.buildPayload();
      const purchaseByFeature = this.determinePurchaseMethod();

      purchaseByFeature(info)
        .then(async (res) => {
          this.setBillingInfo({
            ...this.billingInfo,
            transactionId: res.data.transactionId,
          });

          if (this.usingWeChat) {
            const { redirectUrl } = res.data;

            if (redirectUrl) window.location = redirectUrl;
          } else if ("intentSecret" in res.data) {
            const stripe = await loadStripe(
              this.stripeKey,
              res.data.stripeAccountId
                ? { stripeAccount: res.data.stripeAccountId }
                : null
            );

            return stripe
              .confirmCardPayment(res.data.intentSecret)
              .then((result) => {
                // Handle result.error or result.paymentIntent
                const status = get(result, "paymentIntent.status");

                if (status === "succeeded" || status === "requires_capture") {
                  this.setPurchaseInfo(res.data.giftcard || res.data);
                  this.inprogress = false;

                  this.$bus.$emit(
                    "showAlert",
                    this.$vuetify.lang.t("$vuetify.paymentDetails.accepted"),
                    "primary"
                  );

                  this.$router.push({
                    name: "Confirmation",
                    params: { storeId: this.storeId },
                  });
                } else {
                  this.inprogress = false;
                  this.$bus.$emit(
                    "showAlert",
                    get(
                      result,
                      "error.message",
                      this.$vuetify.lang.t(
                        "$vuetify.paymentDetails.paymentFailed"
                      )
                    ),
                    "error",
                    10000
                  );
                }
              });
          } else {
            this.inprogress = false;
            this.setPurchaseInfo(res.data.giftcard || res.data);

            this.$bus.$emit(
              "showAlert",
              this.$vuetify.lang.t("$vuetify.paymentDetails.accepted"),
              "primary"
            );

            this.$router.push({
              name: "Confirmation",
              params: { storeId: this.storeId },
            });
          }
        })
        .catch((error) => {
          this.inprogress = false;
          const errorMsg =
            get(error, "res.data.errorMsg") ||
            get(error, "response.data.errorMsg");
          const errorCode =
            get(error, "res.data.errorCode") ||
            get(error, "response.data.errorCode");
          const message = errorMsg || errorCode;

          if (message)
            this.$bus.$emit("showAlert", ("" + message).split("code:")[0]);
        });
    },
    purchaseCard() {
      if (this.inprogress) {
        this.$bus.$emit(
          "showAlert",
          this.$vuetify.lang.t("$vuetify.paymentDetails.inProgress")
        );
      } else if (!this.infoEntered) {
        this.$bus.$emit(
          "showAlert",
          this.$vuetify.lang.t("$vuetify.paymentDetails.cardDetailsMissing")
        );
      } else {
        this.makePurchase();
      }
    },

    checkValidation() {
      // Run the validation method of each input
      for (const ref in this.$refs) {
        if (ref && this.$refs[ref] && "validate" in this.$refs[ref]) {
          this.$refs[ref].validate();
        }
      }

      // Country / postal code is used if using a preset card (since they would have already been entered)
      if (!this.paymentCards.length) {
        if (!this.billingInfo.country)
          this.$set(
            this.errors,
            "country",
            this.$vuetify.lang.t("$vuetify.forms.checkout.selectCountry")
          );

        if (!this.billingInfo.postalCode)
          this.$set(
            this.errors,
            "postalCode",
            this.$vuetify.lang.t("$vuetify.forms.checkout.enterPostalCode")
          );
      }

      // Show a snackbar message to make sure the user knows there was an error
      if (Object.keys(this.errors).length)
        this.$bus.$emit(
          "showAlert",
          this.$vuetify.lang.t("$vuetify.forms.checkout.fillMissingFields"),
          "error"
        );
    },

    handleCountryChange() {
      this.billingInfo.country = this.selectedCountry.code;
      this.billingInfo.postalCode = null;

      this.$delete(this.errors, "country");
      this.$delete(this.errors, "postalCode");
    },

    ...mapMutations({
      setBillingInfo: "setBillingInfo",
      setPurchaseInfo: "setPurchaseInfo",
    }),
  },

  async mounted() {
    // Set selected to the users cached selection, else default to the stores country
    if (this.billingInfo && !!this.billingInfo.country) {
      this.selectedCountry = this.countryImport.find(
        (c) => c.code === this.billingInfo.country
      );
    } else if (this.build?.country) {
      this.selectedCountry = this.countryImport.find(
        (c) => c.code === this.build.country
      );
    }

    this.billingInfo.country = this.selectedCountry.code;

    this.$set(this.billingInfo, "paymentMethodId", null);

    await this.fetchPaymentMethods();

    this.initPaymentOptions();

    // Initialize Apple Pay if available
    if (this.isApplePayAvailable) {
      this.initializeApplePay();
    }

    this.$bus.$on("logout", () => {
      this.paymentCards = [];
    });

    // Add click outside handler to close payment select menu
    this.handleClickOutside = (event) => {
      if (this.$refs.paymentSelect && this.$refs.paymentSelect.isMenuActive) {
        const selectElement = this.$refs.paymentSelect.$el;
        const containerElement = document.getElementById(
          "payment-option-container"
        );
        const menuElement = containerElement
          ? containerElement.querySelector(".v-menu__content")
          : null;

        // Check if click is outside both the select and the menu
        const isClickInsideSelect =
          selectElement && selectElement.contains(event.target);
        const isClickInsideMenu =
          menuElement && menuElement.contains(event.target);

        if (!isClickInsideSelect && !isClickInsideMenu) {
          // Close the menu
          this.$refs.paymentSelect.isMenuActive = false;
        }
      }
    };
    // Use nextTick to ensure the handler is added after the component is fully mounted
    this.$nextTick(() => {
      document.addEventListener("click", this.handleClickOutside);
    });
  },
  beforeDestroy() {
    if (this.handleClickOutside) {
      document.removeEventListener("click", this.handleClickOutside);
    }
  },
};
</script>

<style scoped>
.checkout-container {
  text-align: left;

  .checkout-cart-row {
    align-items: stretch;
    gap: 16px;
    display: flex;
    justify-content: space-between;
  }

  .checkout-card-image {
    border-radius: 16px;
    overflow: hidden;
    flex-shrink: 0;
  }

  .checkout-cart-info {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex: 1;
    min-height: 160px;
  }

  .checkout-card-type-desktop {
    color: #111827;
    margin-bottom: 16px;
    text-align: right;
    font-size: 24px;
    line-height: 36px;
    font-weight: 500;
  }

  .checkout-card-type-mobile {
    color: #111827;
    margin-bottom: 16px;
    text-align: left;
    font-size: 16px;
    line-height: 24px;
    font-weight: 500;
    display: none;
  }

  .checkout-subtotal {
    display: flex;
    flex-direction: column;
    text-align: right;
    font-size: 24px;
    line-height: 36px;
  }

  .checkout-subtotal-label {
    font-size: 16px;
    color: #111827;
    margin-bottom: 4px;
    font-weight: 500;
  }

  .checkout-subtotal-amount {
    font-size: 36px;
    line-height: 56px;
    font-weight: 700;
    color: #111827;
  }

  .checkout-detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .checkout-message {
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  #addPaymentMethod {
    margin: auto;
  }

  .checkout-bonus-value {
    color: var(--primary-color);
    font-size: 24px;
    line-height: 36px;
    font-weight: 700;
    text-align: right;
  }

  .back-to-all-gift-cards {
    display: flex;
    align-items: center;
    gap: 14px;
    font-size: 15px;
    line-height: 24px;
    font-weight: 600;
  }

  @media screen and (max-width: 798px) {
    .checkout-cart-row {
      flex-direction: column;
      align-items: center;
    }

    .checkout-cart-info {
      width: 100%;
      margin-top: 16px;
      min-height: auto;
    }

    .checkout-card-image {
      width: 100%;
      max-width: 240px;
    }

    .checkout-card-type-mobile {
      display: block;
    }

    .checkout-card-type-desktop {
      display: none;
    }

    .checkout-subtotal {
      flex-direction: row !important;
      justify-content: space-between;
      align-items: center;
    }
  }
}
</style>
